import { ReactNode, createContext, useContext, useState } from 'react';
import { ConfirmationDialogue, ConfirmationDialogueProps } from 'vopay';

export type Status = 'alert' | 'success' | 'error' | 'none';
export type DialogCloseType = 'confirm' | 'cancel' | 'close';

type DialogMethod = (title: string, options?: Options) => Promise<DialogCloseType>;

type ConfirmDialogCtx = {
  open: DialogMethod;
  error: DialogMethod;
};

type Options = {
  subTitle?: ReactNode;
  subInfo?: string;
  content?: string;
  width?: string;
  buttonColor?: 'primary' | 'secondary' | 'outlined' | 'none' | 'hover';
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  buttonWidth?: string;
  buttonRound?: boolean;
  buttonCancelColor?: 'primary' | 'secondary' | 'outlined' | 'none' | 'hover';
  buttonCancelTextColor?: string;
  renderContent?: ReactNode;
  hasCloseButton?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
};

export const ConfirmDialogContext = createContext<ConfirmDialogCtx>({
  open: () => {
    throw new Error('ConfirmDialog.Provider not found');
  },
  error: () => {
    throw new Error('ConfirmDialog.Provider not found');
  },
});

type BaseDialogueProps = Omit<ConfirmationDialogueProps, 'isVisible'>;

export const ConfirmDialogProvider = ({ children }: { children: ReactNode }) => {
  const [dialogProps, setDialogProps] = useState<BaseDialogueProps>({
    title: '',
    status: 'none',
  });

  const [isOpen, setIsOpen] = useState(false);

  const open = async (title: string, options: Options = {}) =>
    new Promise<DialogCloseType>((res) => {
      setDialogProps(
        createProps(title, 'none', res, {
          ...options,
        }),
      );

      setIsOpen(true);
    });

  const error = async (title: string, options: Options = {}) =>
    new Promise<DialogCloseType>((res) => {
      setDialogProps(
        createProps(title, 'error', res, {
          ...options,
        }),
      );

      setIsOpen(true);
    });

  function createProps(
    title: string,
    status: Status,
    res: (value: DialogCloseType | PromiseLike<DialogCloseType>) => void,
    options: Options,
  ) {
    // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
    const _dialogProps: BaseDialogueProps = {
      ...options,
      title,
      status,
    };

    if (options.hasCloseButton) {
      _dialogProps.close = {
        action: () => {
          setIsOpen(false);
          res('close');
        },
      };
    }

    if (options.confirmLabel) {
      _dialogProps.confirm = {
        label: options.confirmLabel,
        action: () => {
          setIsOpen(false);
          res('confirm');
        },
      };
    }

    if (options.cancelLabel) {
      _dialogProps.cancel = {
        label: options.cancelLabel,
        action: () => {
          setIsOpen(false);
          res('cancel');
        },
      };
    }

    if (options.renderContent) {
      _dialogProps.element = options.renderContent;
    }

    return _dialogProps;
  }

  return (
    <ConfirmDialogContext.Provider value={{ open, error }}>
      {children}
      <ConfirmationDialogue {...dialogProps} isVisible={isOpen} />
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirmDialog = () => useContext(ConfirmDialogContext);
